import { routing } from '@/i18n/routing';
import { isLoggedInFromSession } from '@/utils/routing/is-logged-in';

// EWM-535 — the credential-entry actions legitimately return 401 during their
// own flows (e.g. wrong password on login, unauthenticated change-password
// attempt). A mid-session 401 from any OTHER action means the session ended
// (expired/cleared) → send the user to the login drawer with the
// session-expired banner.
//
// Only 401 (and the matching `account_authentication_error` name) counts as a
// session failure. 403 (Forbidden) is an AUTHORIZATION denial, not an expired
// session — the checkout action returns 403 for business rules (credit stopped,
// pay-on-account not permitted, over credit limit). Treating 403 as expiry would
// hijack those checkout errors and bounce the customer to a misleading
// "session expired" login screen.
//
// Action names are verified against AccountActions.ts (the actual `actionName`
// strings passed to sdk.callAction, not the method names on the AccountActions
// type):
//   login → 'account/login'
//   logout → 'account/logout'
//   register → 'account/register'
//   confirm → 'account/confirm'
//   requestPasswordReset → 'account/requestReset'
//   resetPassword → 'account/reset'
//   changePassword → 'account/password'  ← NOT 'account/changePassword'
//   deleteAccount → 'account/deleteAccount' — EWM-804: throws
//   AccountAuthenticationError (AccountController.ts) on a user-typed wrong
//   password, same as login. Pre-EWM-804 that answered 503 with no errorName
//   (never redirected here); now it's 401 + errorName, so without this entry
//   a wrong password on account deletion would falsely bounce the user to
//   "your session has expired". Currently unreachable in production (its only
//   consumer is the `settings` tastic, disabled via DISABLED_ROUTE_SEGMENTS
//   per EWM-781) but the route returns post-launch.
const AUTH_FLOW_ACTIONS = new Set<string>([
  'account/login',
  'account/register',
  'account/confirm',
  'account/requestReset',
  'account/reset',
  'account/password',
  'account/logout',
  'account/deleteAccount',
]);

export const sessionExpiryRedirectUrl = (
  err: { statusCode?: number; errorName?: string; actionName?: string },
  pathname: string,
  search: string,
  sessionToken?: string,
): string | null => {
  const isAuthFailure = err.statusCode === 401 || err.errorName === 'account_authentication_error';
  if (!isAuthFailure) return null;
  if (err.actionName && AUTH_FLOW_ACTIONS.has(err.actionName)) return null;
  // Loop guard: if the login tray is already open (auth=login present), don't
  // bounce again. Checking `auth=login` rather than only `sessionExpired=1`
  // also covers the guest target (`/?auth=login`, no badge) — otherwise a
  // repeat 401 while already on that URL would re-trigger window.location.assign
  // in a loop. Strip the leading '?' if present before parsing.
  const searchNormalized = search.startsWith('?') ? search.slice(1) : search;
  if (new URLSearchParams(searchNormalized).get('auth') === 'login') return null;

  const first = pathname.split('/').filter(Boolean)[0];
  const hasLocalePrefix = !!first && (routing.locales as readonly string[]).includes(first);
  const prefix = hasLocalePrefix ? `/${first}` : '';

  // Only badge the drawer "session expired" when the client cookie carries an
  // accountId — i.e. there WAS an authenticated session that the BFF has now
  // rejected. A guest who trips a protected action mid-session has no accountId
  // in the cookie, so they get a plain login prompt instead of a confusing
  // "your session has expired" message. (UX-only heuristic; the BFF remains the
  // authz boundary — mirrors the edge-proxy logic.)
  const hadSession = isLoggedInFromSession(sessionToken);
  return `${prefix}/?auth=login${hadSession ? '&sessionExpired=1' : ''}`;
};
